import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  Ban,
  CheckCircle2,
  Clock,
  DoorClosed,
  LogIn,
  MailX,
  UserX,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/loader'
import { OutcomeCard } from '@/components/ui/outcome-card'
import type { TokenOutcomeKind } from '@/lib/types/token-outcome'
import { useRequestNewLink } from '@/hooks/useRequestNewLink'
import { useTranslation } from 'react-i18next'
import { routeDeclares, type TokenRouteId } from '@/lib/token-routes'

/**
 * TokenGate — the one answer to «I followed a link; what happened?»
 *
 * Frames 45 and 48, task 7. Eight routes in this app open on a token, and
 * each grew its own ladder of `if`s with its own colours, its own icons and
 * its own idea of how many ways a link can fail. This is the shared ladder.
 *
 * It is a MOVE, not an invention: `<OutcomeCard>` was already the visual
 * (twelve callers) and `AcceptInvitePage` already had the most complete
 * ladder. What was missing is that nothing stopped a route deciding a dead
 * link was, say, indigo.
 *
 * ## The route cannot override the colour
 *
 * Tone and icon are derived from the outcome HERE and are not props. That is
 * the point of the component — «utløpt» must look the same on the invite
 * route, the offer route and the poll route, or the tone stops carrying
 * meaning. A caller who disagrees with a colour is disagreeing with the
 * severity scale (HOUSE-RULES §2), which is a conversation about the rule,
 * not about one page.
 *
 * ## The exit is a shape, not a slot
 *
 * `action` is `{ label, to | onClick }`, not a `ReactNode`. Handing the
 * caller a slot means handing them the button size, and the 44px exit is a
 * requirement of this surface (`size="lg"` is `--btn-height-lg` = 3rem =
 * 48px, so it clears the floor by construction rather than by review).
 *
 * ## Ten outcomes
 *
 * The union lives in `lib/types/token-outcome.ts` (task 8) and is imported,
 * not restated — the gate and the reference route now read the same list, so a
 * ninth way for a link to fail cannot exist on one side only.
 *
 * `loading` renders a spinner and `ready` renders `children` — those two are
 * not outcomes to report but states to pass through, which is what makes this
 * a gate rather than a message.
 */

type Tone = 'success' | 'error' | 'warning' | 'info'

/**
 * Outcome → severity, in one place.
 *
 * Read against HOUSE-RULES §2: yellow is «det finnes en utvei og ingenting er
 * tapt», red is «ingen vei videre». So `expired`, `used` and `closed` are
 * yellow — the link is spent but the thing behind it still exists and the
 * sender can act — while `revoked` and `invalid` are red, because no action
 * by this user reaches the thing at all.
 */
const OUTCOME: Record<Exclude<TokenOutcomeKind, 'loading' | 'ready'>, { tone: Tone; icon: LucideIcon }> = {
  submitted: { tone: 'success', icon: CheckCircle2 },
  used: { tone: 'warning', icon: CheckCircle2 },
  expired: { tone: 'warning', icon: Clock },
  closed: { tone: 'warning', icon: DoorClosed },
  mismatch: { tone: 'warning', icon: UserX },
  revoked: { tone: 'error', icon: Ban },
  invalid: { tone: 'error', icon: MailX },
  'not-logged-in': { tone: 'info', icon: LogIn },
}

export interface TokenGateAction {
  label: string
  /** Internal route. Mutually exclusive with `onClick`. */
  to?: string
  /** Router state for `to` (e.g. `{ from: location }`, so sign-in returns here). */
  state?: unknown
  onClick?: () => void
}

/** Who sent this, and how to reach them when the link is spent. */
export interface TokenGateSender {
  name?: string
  email?: string
  phone?: string
}

export function TokenGate({
  outcome,
  title,
  description,
  action,
  secondaryAction,
  sender,
  loadingLabel,
  route,
  reissue = false,
  children,
}: {
  outcome: TokenOutcomeKind
  /**
   * Offer «Be om ny lenke» on a dead link when no action is given. Opt-in: only a link an
   * organiser sent can be re-sent by one (an SMS opt-out code cannot).
   */
  reissue?: boolean
  /** The page's `h1` — `OutcomeCard` renders it as one. */
  title?: string
  description?: ReactNode
  /** The 44px exit. */
  action?: TokenGateAction
  secondaryAction?: TokenGateAction
  sender?: TokenGateSender
  loadingLabel?: string
  /**
   * Task 10 · which of the eight token routes this is.
   *
   * `rounds/62.md` criterion 1 ends «Porten leser tabellen», and this is the
   * gate reading it. Passing a `route` turns `TOKEN_ROUTE_OUTCOMES` from a
   * document into a check: render an outcome the route never declared and you
   * hear about it, in development, at the moment it happens. Optional so the
   * 404 and other non-route callers are not forced to invent an id.
   */
  route?: TokenRouteId
  /** Rendered only when `outcome === 'ready'`. */
  children?: ReactNode
}) {
  // Dev-only, and deliberately a warning rather than a throw: a token route
  // showing the WRONG outcome is a bug worth shouting about, but a blank screen
  // for someone holding a dead link would be a worse one.
  if (import.meta.env.DEV && route && !routeDeclares(route, outcome)) {
    console.warn(
      `[TokenGate] route "${route}" rendered outcome "${outcome}", which it does not declare in TOKEN_ROUTE_OUTCOMES. ` +
        'Either the route can genuinely produce it (add it to the table, with why) or this is the bug the table exists to catch.',
    )
  }

  if (outcome === 'ready') return <>{children}</>

  if (outcome === 'loading') {
    return (
      <div className="text-center" aria-live="polite" aria-busy="true">
        <Spinner className="mx-auto mb-4" />
        {loadingLabel && (
          <p className="text-body text-[var(--text-secondary)]">{loadingLabel}</p>
        )}
      </div>
    )
  }

  const { tone: baseTone, icon } = OUTCOME[outcome]
  // House rule 1: red is a dead end. With a way out on the page (a new link, an action) the same
  // outcome is yellow, as the public form already drew it (sweep 24.10.2026).
  const tone: Tone = baseTone === 'error' && (action || reissue) ? 'warning' : baseTone

  return (
    <DeadEndOutcome
      outcome={outcome}
      tone={tone}
      icon={icon}
      title={title}
      description={description}
      action={action}
      secondaryAction={secondaryAction}
      sender={sender}
      reissue={reissue}
    />
  )
}

/**
 * A spent or wrong link always has a way out (03.10.2026 re-score: four token pages had nothing
 * to click). With `reissue`, and no action of the caller's own for `invalid`, `expired` or `revoked`, the gate
 * offers «Be om ny lenke» (`useRequestNewLink`), which queues an ask on the organiser's list and
 * sends nothing, plus «Hva er EventRipple?» for a stranger.
 */
function DeadEndOutcome({
  outcome,
  tone,
  icon,
  title,
  description,
  action,
  secondaryAction,
  sender,
  reissue,
}: {
  outcome: Exclude<TokenOutcomeKind, 'loading' | 'ready'>
  reissue: boolean
  tone: Tone
  icon: LucideIcon
  title?: string
  description?: ReactNode
  action?: TokenGateAction
  secondaryAction?: TokenGateAction
  sender?: TokenGateSender
}) {
  const { t } = useTranslation()
  const { asking, asked, request } = useRequestNewLink()
  const deadLink = outcome === 'invalid' || outcome === 'expired' || outcome === 'revoked'
  if (reissue && deadLink && !action) {
    action = asked
      ? undefined
      : { label: asking ? t('common.loading') : t('notFoundPage.tokenCta'), onClick: () => void request() }
    secondaryAction ??= { label: t('notFoundPage.whatIsEventRipple'), to: '/' }
    if (asked) description = t('notFoundPage.tokenAsked')
  }

  return (
    <OutcomeCard icon={icon} tone={tone} title={title ?? ''} description={description}>
      {(action || secondaryAction || sender) && (
        <div className="space-y-3">
          {action && <GateButton action={action} />}
          {secondaryAction && <GateButton action={secondaryAction} variant="secondary" />}
          {sender && <SenderLine sender={sender} />}
        </div>
      )}
    </OutcomeCard>
  )
}

function GateButton({
  action,
  variant = 'primary',
}: {
  action: TokenGateAction
  variant?: 'primary' | 'secondary'
}) {
  // size="lg" is --btn-height-lg (3rem). The 44px exit is not the caller's to
  // get wrong.
  if (action.to) {
    return (
      <Button asChild variant={variant} size="lg" className="w-full">
        <Link to={action.to} state={action.state}>{action.label}</Link>
      </Button>
    )
  }
  return (
    <Button type="button" variant={variant} size="lg" className="w-full" onClick={action.onClick}>
      {action.label}
    </Button>
  )
}

/**
 * The contact path is a LINK, not a printed address. A spent link is exactly
 * the moment someone needs to reach a human, and «send an email to
 * kari@…» typed as text is a phone-keyboard transcription exercise.
 */
function SenderLine({ sender }: { sender: TokenGateSender }) {
  const { name, email, phone } = sender
  if (!name && !email && !phone) return null
  return (
    <p className="text-small text-[var(--text-secondary)]">
      {name && <span>{name}</span>}
      {name && (email || phone) && <span aria-hidden="true"> · </span>}
      {email && (
        <a className="link-brand" href={`mailto:${email}`}>
          {email}
        </a>
      )}
      {email && phone && <span aria-hidden="true"> · </span>}
      {phone && (
        <a className="link-brand" href={`tel:${phone.replace(/\s+/g, '')}`}>
          {phone}
        </a>
      )}
    </p>
  )
}
